// STORE
import { createEffect, createEvent, createStore } from "effector";
import { EventStateStoreT } from "@interfaces/common";
import InternalStudyStudentsService from "@services/internal-study-students-service";
import {
  StudentShortDataT,
  StudentCoursesDataT,
  StudentCourseLessonDataT,
  IncrementStageCoursePropsT,
} from "@interfaces/internal-study/students";
import { resetAllStates } from "@store/user-store";

// LIST DATA
export const setInternalStudents = createEvent<StudentShortDataT[]>();
export const setInternalStudent = createEvent<StudentShortDataT>();
export const setInternalStudentCourses = createEvent<StudentCoursesDataT[]>();
export const setInternalStudentCourse = createEvent<StudentCoursesDataT>();
export const setInternalStudentLesson = createEvent<StudentCourseLessonDataT>();
export const resetInternalStudentCourse = createEvent();

export const $InternalStudents = createStore<StudentShortDataT[]>([])
  .on(setInternalStudents, (_, newState) => newState)
  .reset(resetAllStates);

export const $InternalStudent = createStore<StudentShortDataT>(
  {} as StudentShortDataT
)
  .on(setInternalStudent, (_, newState) => newState)
  .reset(resetAllStates);

export const $InternalStudentCourses = createStore<StudentCoursesDataT[]>([])
  .on(setInternalStudentCourses, (_, newState) => newState)
  .reset(resetAllStates);

export const $InternalStudentCourse = createStore<StudentCoursesDataT>(
  {} as StudentCoursesDataT
)
  .on(setInternalStudentCourse, (_, newState) => newState)
  .reset(resetInternalStudentCourse);

export const $InternalStudentLesson = createStore<StudentCourseLessonDataT>(
  {} as StudentCourseLessonDataT
)
  .on(setInternalStudentLesson, (_, newState) => newState)
  .reset(resetAllStates);

// LIST STATES
export const setInternalStudentsStateLoading = createEvent<boolean>();
export const setInternalStudentsStateError = createEvent<boolean>();
const setInternalStudentsStateFetched = createEvent<boolean>();
const setInternalStudentsStates = createEvent<EventStateStoreT>();

// INFO STATES
export const setInternalStudentStateLoading = createEvent<boolean>();
export const setInternalStudentStateError = createEvent<boolean>();
export const setInternalStudentStateFetched = createEvent<boolean>();
export const setInternalStudentStates = createEvent<EventStateStoreT>();

// COURSES STATES
export const setInternalStudentCoursesStateLoading = createEvent<boolean>();
export const setInternalStudentCoursesStateError = createEvent<boolean>();
export const setInternalStudentCoursesStateFetched = createEvent<boolean>();
export const setInternalStudentCoursesStates = createEvent<EventStateStoreT>();

// COURSE STATES
export const setInternalStudentCourseStateLoading = createEvent<boolean>();
export const setInternalStudentCourseStateError = createEvent<boolean>();
export const setInternalStudentCourseStateFetched = createEvent<boolean>();
export const setInternalStudentCourseStates = createEvent<EventStateStoreT>();

// LESSON STATES
export const setInternalStudentLessonStateLoading = createEvent<boolean>();
export const setInternalStudentLessonStateError = createEvent<boolean>();
export const setInternalStudentLessonStateFetched = createEvent<boolean>();
export const setInternalStudentLessonStates = createEvent<EventStateStoreT>();

// STUDENTS
export const $InternalStudentsState = createStore<EventStateStoreT>({
  isLoading: true,
  error: false,
  isFetched: false,
})
  .on(setInternalStudentsStateLoading, (oldState, newState) => ({
    ...oldState,
    isLoading: newState,
  }))
  .on(setInternalStudentsStateError, (oldState, newState) => ({
    ...oldState,
    isLoading: false,
    error: true,
  }))
  .on(setInternalStudentsStateFetched, (oldState, newState) => ({
    ...oldState,
    isFetched: newState,
  }))
  .on(setInternalStudentsStates, (_, newState) => newState)
  .reset(resetAllStates);

// STUDENT
export const $InternalStudentState = createStore<EventStateStoreT>({
  isLoading: true,
  error: false,
  isFetched: false,
})
  .on(setInternalStudentStateLoading, (oldState, newState) => ({
    ...oldState,
    isLoading: newState,
  }))
  .on(setInternalStudentStateError, (oldState, newState) => ({
    ...oldState,
    isLoading: false,
    error: true,
  }))
  .on(setInternalStudentStateFetched, (oldState, newState) => ({
    ...oldState,
    isFetched: newState,
  }))
  .on(setInternalStudentStates, (_, newState) => newState)
  .reset(resetAllStates);

// COURSES
export const $InternalStudentCoursesState = createStore<EventStateStoreT>({
  isLoading: true,
  error: false,
  isFetched: false,
})
  .on(setInternalStudentCoursesStateLoading, (oldState, newState) => ({
    ...oldState,
    isLoading: newState,
  }))
  .on(setInternalStudentCoursesStateError, (oldState, newState) => ({
    ...oldState,
    isLoading: false,
    error: true,
  }))
  .on(setInternalStudentCoursesStateFetched, (oldState, newState) => ({
    ...oldState,
    isFetched: newState,
  }))
  .on(setInternalStudentCoursesStates, (_, newState) => newState)
  .reset(resetAllStates);

// COURSE
export const $InternalStudentCourseState = createStore<EventStateStoreT>({
  isLoading: true,
  error: false,
  isFetched: false,
})
  .on(setInternalStudentCourseStateLoading, (oldState, newState) => ({
    ...oldState,
    isLoading: newState,
  }))
  .on(setInternalStudentCourseStateError, (oldState, newState) => ({
    ...oldState,
    isLoading: false,
    error: true,
  }))
  .on(setInternalStudentCourseStateFetched, (oldState, newState) => ({
    ...oldState,
    isFetched: newState,
  }))
  .on(setInternalStudentCourseStates, (_, newState) => newState)
  .reset(resetInternalStudentCourse);

// LESSON
export const $InternalStudentLessonState = createStore<EventStateStoreT>({
  isLoading: true,
  error: false,
  isFetched: false,
})
  .on(setInternalStudentLessonStateLoading, (oldState, newState) => ({
    ...oldState,
    isLoading: newState,
  }))
  .on(setInternalStudentLessonStateError, (oldState, newState) => ({
    ...oldState,
    isLoading: false,
    error: true,
  }))
  .on(setInternalStudentLessonStateFetched, (oldState, newState) => ({
    ...oldState,
    isFetched: newState,
  }))
  .on(setInternalStudentLessonStates, (_, newState) => newState)
  .reset(resetAllStates);

export const fetchInternalStudents = createEffect(async (companyId: number) => {
  InternalStudyStudentsService.getAll(companyId, (err, res) => {
    if (err || !res) {
      return setInternalStudentsStateError(true);
    }
    setInternalStudents(res.data);
    setInternalStudentsStates({
      isLoading: false,
      error: false,
      isFetched: false,
    });
  });
});

export const removeInternalList = createEffect<(p: { id: number }) => void>(
  async ({ id }) => {
    InternalStudyStudentsService.delete(id, (err, res) => {
      if (err || !res) {
        return console.error("При удалении обучающегося произошла ошибка");
      }
      const lists = $InternalStudents.getState();
      const listIndex = lists.findIndex((c) => c.id === id);

      lists.splice(listIndex, 1);
      setInternalStudents(Array.from(lists));
    });
  }
);

// Student Courses
export const fetchInternalStudentCourses = createEffect(async () => {
  InternalStudyStudentsService.getAllCourses((err, res) => {
    if (err || !res) {
      return setInternalStudentCoursesStateError(true);
    }
    setInternalStudentCourses(res.data);
    setInternalStudentCoursesStates({
      isLoading: false,
      error: false,
      isFetched: false,
    });
  });
});

export const fetchInternalStudentCourse = createEffect(async (id: number) => {
  InternalStudyStudentsService.getCourse(id, (err, res) => {
    if (err || !res) {
      return setInternalStudentCourseStateError(true);
    }
    setInternalStudentCourse(res.data);
    setInternalStudentCourseStates({
      isLoading: false,
      error: false,
      isFetched: true,
    });
  });
});

export const fetchInternalStudentLesson = createEffect(async (id: number) => {
  InternalStudyStudentsService.getLesson(id, (err, res) => {
    if (err || !res) {
      return setInternalStudentLessonStateError(true);
    }
    setInternalStudentLesson(res.data);
    setInternalStudentLessonStates({
      isLoading: false,
      error: false,
      isFetched: true,
    });
  });
});

export const stageIncrementInternalStudentLesson =
  createEffect<IncrementStageCoursePropsT>(async ({ id, cb }) => {
    InternalStudyStudentsService.incrementStage(id, (err, res) => {
      if (err || !res) {
        return setInternalStudentLessonStateError(true);
      }
      setInternalStudentCourse(res.data);
      setInternalStudentCourseStates({
        isLoading: false,
        error: false,
        isFetched: true,
      });
      cb(err, res);
    });
  });

export const finishInternalStudentCourse =
  createEffect<IncrementStageCoursePropsT>(async ({ id, cb }) => {
    InternalStudyStudentsService.finish(id, (err, res) => {
      if (err || !res) {
        return setInternalStudentLessonStateError(true);
      }
      setInternalStudentCourse(res.data);
      setInternalStudentCourseStates({
        isLoading: false,
        error: false,
        isFetched: true,
      });
      cb(err, res);
    });
  });
